/**
 * IMPORTS
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Undo2 } from "lucide-react";

// components
import { Button } from "@components/button/button";
import { ImagePreviewModal } from "@components/image-preview-modal";
import { DynamicTable } from "@components/table-custom/table";
import { Tabs } from "@components/tabs";

// components / pages
import {
  dataTitleRemessaProgress,
  defaultImageUrl,
} from "@pages/client/details-shipment-in-progress/helpers";

// store-zustand
import { useStoreZustandRemessaEmAndamento } from "@/store-zustand/cliente/remessas-em-andamento";

const DetailsShipmentInProgress = () => {
  const router = useNavigate();
  const [imageToPreview, setImageToPreview] = useState<string | null>(null);

  const { remessaEmAndamentoUnico, clearPersistedData } = useStoreZustandRemessaEmAndamento();

  const remessa = remessaEmAndamentoUnico?.modelo ?? null;

  /**Função para abrir a imagem em preview */
  const handleOpenPreview = (imageUrl?: string | null) => {
    if (!imageUrl || imageUrl.trim() === "") {
      return;
    }

    setImageToPreview(imageUrl);
  };

  return (
    <Tabs defaultValue="all">
      <div className="flex items-center">
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="default"
            className="h-8 gap-1 text-neutral-50"
            onClick={() => {
              // Redireciona para a página de remessas em andamento
              // e limpa os dados persistidos
              clearPersistedData();
              if (window.history.length > 1) {
                router(-1);
                return;
              }

              router("/shipments-in-progress");
            }}
          >
            <Undo2 className="h-3.5 w-3.5" />
            <span className="sm:whitespace-nowra sr-only text-neutral-50 sm:not-sr-only">
              Voltar
            </span>
          </Button>
        </div>
      </div>
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="mb-4 flex flex-col gap-6 rounded-lg bg-gradient-to-r from-white to-gray-100 p-6 shadow lg:flex-row">
          {/* LADO ESQUERDO */}
          <div className="flex-1">
            <h1 className="mb-4 text-2xl font-bold text-black">
              Remessa {remessaEmAndamentoUnico?.numero_remessa || "N/A"}
            </h1>

            <div className="mb-6 flex gap-6">
              <div className="w-full max-w-[300px] overflow-hidden rounded-xl shadow-md">
                <div className="border-b p-4">
                  <h2 className="text-lg font-semibold text-gray-700">
                    {remessa?.nome_modelo || "N/A"}
                  </h2>
                </div>

                <div className="flex items-center justify-center gap-2 p-4">
                  <img
                    src={remessa?.foto_frente_url ?? defaultImageUrl}
                    alt="Modelo - frente"
                    className="h-28 cursor-pointer rounded object-cover shadow transition hover:scale-105"
                    onClick={() => handleOpenPreview(remessa?.foto_frente_url)}
                  />
                  <img
                    src={remessa?.foto_verso_url ?? defaultImageUrl}
                    alt="Modelo - verso"
                    className="h-28 cursor-pointer rounded object-cover shadow transition hover:scale-105"
                    onClick={() => handleOpenPreview(remessa?.foto_verso_url)}
                  />
                </div>
              </div>
            </div>

            {/* Linha de progresso */}
            <div className="mb-4 flex items-center gap-3 overflow-x-auto">
              {remessaEmAndamentoUnico?.historico_status_remessa.map(
                (
                  step: { id: number; label: string; color: string; active: boolean; date: string },
                  index: number
                ) => (
                  <div className="flex items-center gap-2" key={step.id}>
                    <div className="flex items-center gap-1">
                      <div
                        className={`${step.color} flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold text-white`}
                      >
                        {step?.id ?? "N/A"}
                      </div>
                      <span
                        className={`text-sm font-medium ${
                          step.active ? "text-gray-800" : "text-gray-400"
                        }`}
                      >
                        {step?.label ?? "N/A"}
                      </span>
                    </div>

                    {index < remessaEmAndamentoUnico?.historico_status_remessa?.length - 1 && (
                      <div className="h-0.5 w-6 bg-gray-300" />
                    )}
                  </div>
                )
              )}
            </div>
          </div>

          {/* LADO DIREITO - STATUS */}
          <div className="w-full rounded bg-gray-200 p-4 shadow lg:w-72">
            <h3 className="mb-3 text-sm font-semibold text-gray-700">Status</h3>
            <ul className="space-y-3 text-sm">
              {remessaEmAndamentoUnico?.historico_status_remessa.map(
                (step: {
                  id: number;
                  label: string;
                  color: string;
                  active: boolean;
                  date: string;
                }) => (
                  <li key={step.id}>
                    <p className="font-bold text-gray-800">
                      {step.id}) {step.label}
                    </p>
                    <p className="text-xs text-gray-600">{step.date ?? "Data não disponível"}</p>
                  </li>
                )
              )}
            </ul>
          </div>
        </div>

        {/**ITEMS DA REMESSA */}
        <DynamicTable
          title="Items da Remessa"
          description="Abaixo estão listados os itens da remessa."
          isLoadingPage={false}
          data={remessaEmAndamentoUnico?.items_remessa || []}
          offset={0}
          totalItems={1}
          itemsPerPage={10}
          onGoToPage={() => {}}
          columns={dataTitleRemessaProgress}
          paginations={[1]}
          currentPage={1}
          lastPage={1}
          childerSearch={<div />}
          actions={[]}
        />

        {imageToPreview && (
          <ImagePreviewModal
            open={!!imageToPreview}
            onClose={() => setImageToPreview(null)}
            imageUrl={imageToPreview}
          />
        )}
      </div>
    </Tabs>
  );
};

/**
 * EXPORTS
 */
export { DetailsShipmentInProgress };
